import { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type { AddItemDto } from '@azoupvendas/shared';
import { usePedidoStore } from '../stores/pedido.store';

// AIDEV-NOTE: Helpers de formatacao replicados do PedidoDetailPage para consistencia visual
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

interface CarrinhoItemProps {
  item: AddItemDto;
  index: number;
  // AIDEV-NOTE: Políticas de desconto por item — vindas do JWT via Step3
  podeDescontoItem?: boolean;
  limiteDescontoItem?: number;
  // AIDEV-NOTE: Políticas de alteração de preço unitário — vindas do JWT via Step3
  podeAlterarPreco?: boolean;
  precoOriginal?: number; // valor_unitario no momento em que o item foi adicionado
  limiteReducaoPreco?: number | null; // null = sem limite para baixo
  limiteAumentoPreco?: number | null; // null = sem limite para cima
}

// AIDEV-NOTE: Linha de item no carrinho com edicao inline de quantidade, desconto e (opcionalmente) preco unitario.
// Ao alterar, chama pedidoStore.updateItem(index, updates) para manter estado global sincronizado.
// Remocao exige confirmacao via Dialog MUI para evitar exclusao acidental.
// Preco alterado manualmente exibido em warning.main (laranja) com tooltip mostrando preco original e limites.

export function CarrinhoItem({
  item,
  index,
  podeDescontoItem = true,
  limiteDescontoItem = 100,
  podeAlterarPreco = false,
  precoOriginal,
  limiteReducaoPreco = null,
  limiteAumentoPreco = null
}: CarrinhoItemProps) {
  const updateItem = usePedidoStore((s) => s.updateItem);
  const removeItem = usePedidoStore((s) => s.removeItem);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // AIDEV-NOTE: Estado local de string para o campo de preço — evita rejeição silenciosa de valores
  // intermediários (ex: ao digitar "85", o handler via onChange veria "8" primeiro).
  // Validação real acontece apenas no onBlur, revertendo ao valor do store se inválido.
  // Exibicao sempre com 2 casas decimais (toFixed) — o store pode ter mais precisao.
  const [localPreco, setLocalPreco] = useState(Number(item.valor_unitario).toFixed(2));
  const [precoError, setPrecoError] = useState(false);

  // Sincroniza estado local quando o item muda externamente (ex: reset do store)
  const valorUnitarioRef = useRef(item.valor_unitario);
  if (item.valor_unitario !== valorUnitarioRef.current) {
    valorUnitarioRef.current = item.valor_unitario;
    setLocalPreco(Number(item.valor_unitario).toFixed(2));
    setPrecoError(false);
  }

  // AIDEV-NOTE: Total calculado ao vivo = (unitario * qtd) * (1 - desconto/100)
  const totalItem =
    item.valor_unitario * item.quantidade * (1 - (item.desconto_percentual ?? 0) / 100);

  // Detecta se o preço foi alterado manualmente em relação ao original
  const precoFoiAlterado =
    podeAlterarPreco &&
    precoOriginal !== undefined &&
    Math.abs(item.valor_unitario - precoOriginal) > 0.001;

  // Calcula limites para o campo de preço
  const precoMin =
    precoOriginal !== undefined && limiteReducaoPreco !== null
      ? precoOriginal * (1 - limiteReducaoPreco / 100)
      : 0.01;
  const precoMax =
    precoOriginal !== undefined && limiteAumentoPreco !== null
      ? precoOriginal * (1 + limiteAumentoPreco / 100)
      : undefined;

  const handleQuantidadeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseInt(e.target.value, 10);
      if (!isNaN(v) && v >= 1) {
        updateItem(index, { quantidade: v });
      }
    },
    [updateItem, index]
  );

  const handleDescontoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v) && v >= 0 && v <= limiteDescontoItem) {
        updateItem(index, { desconto_percentual: v });
      }
    },
    [updateItem, index, limiteDescontoItem]
  );

  const handlePrecoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalPreco(e.target.value);
    setPrecoError(false);
  }, []);

  const handlePrecoBlur = useCallback(() => {
    // AIDEV-NOTE: Arredonda para 2 casas antes de validar/salvar — preco de venda usa 2 decimais
    const v = Math.round(parseFloat(localPreco) * 100) / 100;
    if (
      isNaN(v) ||
      v <= 0 ||
      v < precoMin - 0.001 ||
      (precoMax !== undefined && v > precoMax + 0.001)
    ) {
      // AIDEV-NOTE: Reverte ao valor atual do store se inválido; mostra borda de erro
      setLocalPreco(Number(item.valor_unitario).toFixed(2));
      setPrecoError(true);
      return;
    }
    setPrecoError(false);
    setLocalPreco(v.toFixed(2));
    updateItem(index, { valor_unitario: v });
  }, [localPreco, item.valor_unitario, precoMin, precoMax, updateItem, index]);

  const handleConfirmRemove = useCallback(() => {
    removeItem(index);
    setConfirmOpen(false);
  }, [removeItem, index]);

  // Tooltip para o campo de preço editável
  const tooltipPreco =
    precoOriginal !== undefined
      ? [
          `Preço original: ${formatCurrency(precoOriginal)}`,
          limiteReducaoPreco !== null
            ? `Redução máxima: ${limiteReducaoPreco}%`
            : 'Sem limite de redução',
          limiteAumentoPreco !== null
            ? `Aumento máximo: ${limiteAumentoPreco}%`
            : 'Sem limite de aumento'
        ].join(' · ')
      : '';

  return (
    <>
      {/* AIDEV-NOTE: mobile = coluna (desc em cima, controles embaixo); sm+ = linha unica */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          gap: { xs: 0.75, sm: 1.5 },
          py: 1.5,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Descricao e detalhes */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {item.descricao}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {item.referencia && (
              <Typography variant="caption" color="text.secondary">
                Ref: {item.referencia}
              </Typography>
            )}
            {item.cor && (
              <Typography variant="caption" color="text.secondary">
                Cor: {item.cor}
              </Typography>
            )}
            {item.tamanho && (
              <Typography variant="caption" color="text.secondary">
                Tam: {item.tamanho}
              </Typography>
            )}
            {/* Exibe preço unitário como texto quando não editável */}
            {!podeAlterarPreco && (
              <Typography variant="caption" color="text.secondary">
                Unit: {formatCurrency(item.valor_unitario)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* AIDEV-NOTE: mobile = campos numa linha e total/remover na linha de baixo, alinhados a
            direita. Antes era uma linha unica de ~414px em ~356px disponiveis: os filhos tinham
            shrink default e encolhiam, cortando os labels ("Q...", "D...") e o valor da qtd. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              flexWrap: { xs: 'wrap', sm: 'nowrap' }
            }}
          >
            {/* Preço unitário editável — visível apenas quando política permite */}
            {podeAlterarPreco && (
              <Tooltip title={tooltipPreco} placement="top">
                <TextField
                  type="number"
                  size="small"
                  label="Preço"
                  value={localPreco}
                  onChange={handlePrecoChange}
                  onBlur={handlePrecoBlur}
                  onFocus={(e) => e.target.select()}
                  error={precoError}
                  inputProps={{
                    min: precoMin,
                    max: precoMax,
                    step: 0.01,
                    inputMode: 'decimal',
                    style: { textAlign: 'center' }
                  }}
                  // AIDEV-NOTE: Cor laranja (warning.main) indica que o preço foi alterado manualmente
                  sx={{
                    width: { xs: 112, sm: 124 },
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root':
                      precoFoiAlterado && !precoError
                        ? {
                            '& fieldset': { borderColor: 'warning.main' },
                            '&:hover fieldset': { borderColor: 'warning.dark' }
                          }
                        : {}
                  }}
                  InputProps={{
                    sx:
                      precoFoiAlterado && !precoError
                        ? { color: 'warning.main', fontWeight: 600 }
                        : {}
                  }}
                />
              </Tooltip>
            )}

            {/* Quantidade */}
            {/* AIDEV-NOTE: Number() remove zeros decimais espurios ("3.0" -> 3); fracao real e mantida */}
            <TextField
              type="number"
              size="small"
              label="Qtd"
              value={Number(item.quantidade)}
              onChange={handleQuantidadeChange}
              onFocus={(e) => e.target.select()}
              inputProps={{
                min: 1,
                inputMode: 'numeric',
                style: { textAlign: 'center', width: 36 }
              }}
              sx={{ width: 72, flexShrink: 0 }}
            />

            {/* Desconto % — oculto se política não permite desconto por item */}
            {podeDescontoItem && (
              <TextField
                type="number"
                size="small"
                label="Desc%"
                value={item.desconto_percentual ?? 0}
                onChange={handleDescontoChange}
                onFocus={(e) => e.target.select()}
                inputProps={{
                  min: 0,
                  max: limiteDescontoItem,
                  step: 0.5,
                  inputMode: 'decimal',
                  style: { textAlign: 'center', width: 36 }
                }}
                sx={{ width: 72, flexShrink: 0 }}
              />
            )}
          </Box>

          {/* AIDEV-NOTE: ml auto joga o grupo para a direita quando ele cai na 2a linha (mobile) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              ml: { xs: 'auto', sm: 0 }
            }}
          >
            {/* Total calculado */}
            <Typography
              variant="body2"
              fontWeight={600}
              color="primary.main"
              sx={{ minWidth: 80, textAlign: 'right' }}
            >
              {formatCurrency(totalItem)}
            </Typography>

            {/* Remover */}
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmOpen(true)}
              aria-label="Remover item"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Dialog de confirmacao de remocao */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Remover item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja remover &quot;{item.descricao}&quot; do carrinho?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
